import clsx from "clsx"
import { forwardRef, memo, useCallback, useEffect, useState } from "react"

import useInView from "@hooks/useInView"

import { getFileCategory, isImage } from "@utils/fileUtils"

import { Avatar } from "@atoms"

import { FILE_CATEGORY_ICONS } from "@constants/fileIcons"

import styles from "./FileThumbnail.module.scss"
import { FileThumbnailProps } from "./FileThumbnail.types"

function FileThumbnail(props: FileThumbnailProps, forwardedRef: React.Ref<HTMLButtonElement>) {
    const {
        mimeType,
        filename,
        thumbnailSrc,
        isInteractive,
        _isFocused,
        _isHovered,
        isPressed,
        isDisabled,
        isSelected,
        onClick,
        className,
        ...rest
    } = props

    const [imageError, setImageError] = useState(false)
    const { ref, isInView } = useInView<HTMLDivElement>()

    const fileCategory = getFileCategory(mimeType, filename)
    const isImageFile = isImage(mimeType)
    const shouldShowThumbnail = isImageFile && thumbnailSrc && !imageError

    const handleImageError = useCallback(() => {
        setImageError(true)
    }, [])

    useEffect(() => {
        setImageError(false)
    }, [thumbnailSrc])

    const renderThumbnailContent = () => {
        if (shouldShowThumbnail) {
            return (
                <div ref={ref}>
                    {isInView ? (
                        <Avatar
                            size="xl"
                            variant="company"
                            src={thumbnailSrc}
                            fallbackName={filename || "File"}
                            baseClassName={styles.image}
                        />
                    ) : (
                        <Avatar
                            size="xl"
                            variant="object"
                            icon={FILE_CATEGORY_ICONS.image}
                            baseClassName={styles.image}
                        />
                    )}
                    {/* Hidden img to detect load errors (Avatar uses background-image which can't detect errors) */}
                    {isInView && (
                        <img src={thumbnailSrc} alt="" style={{ display: "none" }} onError={handleImageError} />
                    )}
                </div>
            )
        } else {
            return (
                <Avatar
                    size="xl"
                    variant="object"
                    icon={FILE_CATEGORY_ICONS[fileCategory]}
                    baseClassName={styles[fileCategory]}
                    iconClassName={styles.icon}
                />
            )
        }
    }

    if (isInteractive) {
        return (
            <button
                ref={forwardedRef}
                type="button"
                className={clsx(
                    "light-theme",
                    styles.interactive,
                    {
                        [styles.isFocused]: _isFocused,
                        [styles.isHovered]: _isHovered,
                        [styles.isPressed]: isPressed,
                        [styles.isDisabled]: isDisabled,
                        [styles.isSelected]: isSelected,
                    },
                    className,
                )}
                onClick={onClick}
                disabled={isDisabled}
                aria-pressed={isSelected}
                {...rest}
            >
                {renderThumbnailContent()}
            </button>
        )
    }

    return renderThumbnailContent()
}

export default memo(forwardRef(FileThumbnail))
